import {
  createContext,
  useReducer,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import { frontendServerGateway } from '../api/api-utils/commonApiUtils';
import { userSchema } from '../api/schemas/user-profile.schema';

import type { Dispatch, ReactNode } from 'react';
import type {
  IUnreadInAppMessageData,
  ISuggestedProductModal,
  BoostModalData,
  SellerAnalyticsModalDisplayType,
  ConsentOptions,
} from '../types';
import type { UserReducerAction } from '../contexts/reducers/userReducer';

import axios from 'axios';
import { userReducer } from '../contexts/reducers/userReducer';
import { useRouter } from 'next/router';
import type { NotificationFrequency } from '../components/SearchPage/hooks/useSavedSearch/useSavedSearch.typed';
import { signIn, signOut, useSession } from 'next-auth/react';
import { fireToast } from '../helpers/Toasts';
import { UserProfile } from '../api/schemas/user-profile.schema';
import { setupAuthInterceptor } from '../api/api-utils/commonApiUtils';
import { isProtectedRoute } from '../constants/protectedRoutes';
import { API } from '../helpers/pages';
import { logger } from '@dist-marketplace-frontend/services';

interface UserProviderProps {
  children: ReactNode;
  initialUser: UserProfile | undefined;
  consentOptions: ConsentOptions;
}

interface IHistoryCheckModalData {
  title?: string;
}

interface UserContextI {
  userProfile: UserProfile | undefined;
  dispatchUserProfile: Dispatch<UserReducerAction>;
  consentOptions: ConsentOptions;
  isInAppMessageSimpleModalOpen: boolean;
  setIsInAppMessageSimpleModalOpen: (t: boolean) => void;
  inAppMessageData?: IUnreadInAppMessageData;
  setInAppMessageData: (t: IUnreadInAppMessageData | undefined) => void;
  isBoostModalOpen: boolean;
  setIsBoostModalOpen: (t: boolean) => void;
  boostModalData: BoostModalData | undefined;
  setBoostModalData: (t: BoostModalData | undefined) => void;
  isSuggestedProductModalOpen: boolean;
  setIsSuggestedProductModalOpen: (t: boolean) => void;
  suggestedProductModalData?: ISuggestedProductModal;
  setSuggestedProductModalData: (t: ISuggestedProductModal | undefined) => void;
  currency: 'EUR' | 'GBP' | undefined;
  setCurrency: (t: 'EUR' | 'GBP' | undefined) => void;
  selectedBundle?: number;
  setSelectedBundle: (bundleID: number | undefined) => void;
  isHistoryCheckModalOpen: boolean;
  setIsHistoryCheckModalOpen: (t: boolean) => void;
  historyCheckModalData: IHistoryCheckModalData | undefined;
  setHistoryCheckModalData: (t: IHistoryCheckModalData | undefined) => void;
  isSavedSearchModalOpen: boolean;
  setIsSavedSearchModalOpen: (t: boolean) => void;
  isInstantAlertAvailable: boolean;
  setIsInstantAlertAvailable: (t: boolean) => void;
  isSellerAnalyticsModalOpen: boolean;
  setIsSellerAnalyticsModalOpen: (t: boolean) => void;
  sellerAnalyticsModalDisplay?: SellerAnalyticsModalDisplayType;
  setSellerAnalyticsModalDisplay: (
    t: SellerAnalyticsModalDisplayType | undefined,
  ) => void;
  isSearchSaved: boolean;
  setIsSearchSaved: (saved: boolean) => void;
  selectedNotificationFrequency?: NotificationFrequency;
  setSelectedNotificationFrequency: (frequency: NotificationFrequency) => void;
  isTrustedDealerModalOpen: boolean;
  setIsTrustedDealerModalOpen: (t: boolean) => void;
  isBrazeInitialized: boolean;
  setIsBrazeInitialized: (t: boolean) => void;
  isUserVerified: boolean;
  refreshUserVerification: () => Promise<void>;
  isWriteOffModalOpen: boolean;
  setIsWriteOffModalOpen: (t: boolean) => void;
  /**
   * Perform a logout.
   * @param options - The options for the logout.
   * @param options.userInitiated - Whether the logout was initiated by the user.
   * This should only be true if the user clicked the signout button in the UI.
   * @param options.callbackUrl - The URL to redirect to after logout.
   */
  performLogout: (options: {
    userInitiated: boolean;
    callbackUrl?: string;
  }) => Promise<void>;
}

const UserContext = createContext<UserContextI>({
  userProfile: undefined,
  dispatchUserProfile: () => null,
  isInAppMessageSimpleModalOpen: false,
  setIsInAppMessageSimpleModalOpen: () => null,
  inAppMessageData: undefined,
  setInAppMessageData: () => null,
  isBoostModalOpen: false,
  setIsBoostModalOpen: () => null,
  boostModalData: undefined,
  setBoostModalData: () => null,
  isSuggestedProductModalOpen: false,
  setIsSuggestedProductModalOpen: () => null,
  suggestedProductModalData: undefined,
  setSuggestedProductModalData: () => null,
  currency: undefined,
  setCurrency: () => null,
  selectedBundle: undefined,
  setSelectedBundle: () => null,
  isHistoryCheckModalOpen: false,
  setIsHistoryCheckModalOpen: () => null,
  historyCheckModalData: undefined,
  setHistoryCheckModalData: () => null,
  isSavedSearchModalOpen: false,
  setIsSavedSearchModalOpen: () => null,
  isInstantAlertAvailable: false,
  setIsInstantAlertAvailable: () => null,
  isSellerAnalyticsModalOpen: false,
  setIsSellerAnalyticsModalOpen: () => null,
  sellerAnalyticsModalDisplay: undefined,
  setSellerAnalyticsModalDisplay: () => null,
  consentOptions: {
    functionalConsent: false,
    allowGoogle: false,
    allowWootric: false,
    allowBraze: false,
    allowConvert: false,
  },
  isSearchSaved: false,
  setIsSearchSaved: () => null,
  selectedNotificationFrequency: undefined,
  setSelectedNotificationFrequency: () => null,
  isTrustedDealerModalOpen: false,
  setIsTrustedDealerModalOpen: () => null,
  isBrazeInitialized: false,
  setIsBrazeInitialized: () => null,
  isUserVerified: false,
  refreshUserVerification: () => Promise.resolve(),
  isWriteOffModalOpen: false,
  setIsWriteOffModalOpen: () => null,
  performLogout: async () => undefined,
});

function UserProvider({
  children,
  initialUser,
  consentOptions,
}: UserProviderProps) {
  const [userProfile, dispatchUserProfile] = useReducer(
    userReducer,
    initialUser,
  );

  const [isInAppMessageSimpleModalOpen, setIsInAppMessageSimpleModalOpen] =
    useState(false);
  const [inAppMessageData, setInAppMessageData] =
    useState<IUnreadInAppMessageData>();
  const [isBoostModalOpen, setIsBoostModalOpen] = useState<boolean>(false);
  const [boostModalData, setBoostModalData] = useState<
    BoostModalData | undefined
  >(undefined);
  const [isSuggestedProductModalOpen, setIsSuggestedProductModalOpen] =
    useState<boolean>(false);
  const [suggestedProductModalData, setSuggestedProductModalData] = useState<
    ISuggestedProductModal | undefined
  >(undefined);
  const [currency, setCurrency] = useState<'EUR' | 'GBP' | undefined>(
    undefined,
  );
  const [selectedBundle, setSelectedBundle] = useState<number | undefined>(
    undefined,
  );
  const [isHistoryCheckModalOpen, setIsHistoryCheckModalOpen] =
    useState<boolean>(false);
  const [historyCheckModalData, setHistoryCheckModalData] = useState<
    IHistoryCheckModalData | undefined
  >(undefined);
  const [isSavedSearchModalOpen, setIsSavedSearchModalOpen] =
    useState<boolean>(false);
  const [isInstantAlertAvailable, setIsInstantAlertAvailable] =
    useState<boolean>(false);
  const [isSellerAnalyticsModalOpen, setIsSellerAnalyticsModalOpen] =
    useState<boolean>(false);
  const [sellerAnalyticsModalDisplay, setSellerAnalyticsModalDisplay] =
    useState<SellerAnalyticsModalDisplayType | undefined>(undefined);
  const [isSearchSaved, setIsSearchSaved] = useState<boolean>(false);
  const [selectedNotificationFrequency, setSelectedNotificationFrequency] =
    useState<NotificationFrequency | undefined>();
  const [isTrustedDealerModalOpen, setIsTrustedDealerModalOpen] =
    useState<boolean>(false);
  const [isBrazeInitialized, setIsBrazeInitialized] = useState<boolean>(false);
  const [isWriteOffModalOpen, setIsWriteOffModalOpen] =
    useState<boolean>(false);

  const isUserVerified = Boolean(
    userProfile?.verification.email || userProfile?.verification.phone,
  );

  const { update: updateSession } = useSession();

  // Pin the unstable values behind refs so the interceptor setup effect runs
  // once — otherwise every session refetch would tear down and recreate the
  // interceptor, resetting its concurrent-refresh lock mid-flight.
  // Replace with useEffectEvent once we're on React 19.
  const updateSessionRef = useRef(updateSession);
  updateSessionRef.current = updateSession;

  const userProfileNameRef = useRef(userProfile?.name || '');
  userProfileNameRef.current = userProfile?.name || '';

  const performLogout = useCallback(
    async ({
      userInitiated,
      callbackUrl,
    }: {
      userInitiated: boolean;
      callbackUrl?: string;
    }) => {
      const signOutAndClear = async () => {
        await signOut(callbackUrl ? { callbackUrl } : { redirect: false });
        dispatchUserProfile({ type: 'CLEAR' });
        if (isProtectedRoute(location.pathname)) {
          location.assign('/');
        } else {
          fireToast({
            type: 'SUCCESS',
            text: `You're now logged out ${userProfileNameRef.current}. Hope to see you soon!`,
          });
        }
      };

      if (userInitiated) {
        try {
          await axios.get('/api/auth/keycloak_signout');
        } catch (error) {
          // Log and fall through — we still want to clear the local session even
          // if the keycloak signout request fails, otherwise the user is left
          // half-authenticated.
          logger.error({
            message: 'Unable to signout',
            error,
            route: API.KEYCLOAK_SIGNOUT,
          });
        }
        await signOutAndClear();
      } else {
        await runOnceAcrossTabs({
          lockName: 'dd-logout',
          callback: async () => {
            try {
              await signOutAndClear();
            } catch (error) {
              logger.error({
                message:
                  'An error occurred while trying to force the user to log out',
                error,
              });
            }
          },
        });
      }
    },
    [dispatchUserProfile],
  );

  useEffect(() => {
    setupAuthInterceptor({
      onSessionRefreshSuccess: async () => {
        // At this point, the next-auth cookie is already refreshed by the interceptor,
        // This connects the interceptor's behavior back to next-auth in the client here.
        await updateSessionRef.current();
      },
      onSessionRefreshError: async () => {
        if (isProtectedRoute(location.pathname)) {
          signIn('keycloak');
          return;
        }
        await performLogout({ userInitiated: false });
      },
    });
  }, [performLogout]);

  const inFlightRef = useRef<Promise<void> | null>(null);

  const refreshUserVerification = useCallback(async (): Promise<void> => {
    // Handle concurrent requests
    if (inFlightRef.current) {
      return inFlightRef.current;
    }

    const promise = (async () => {
      await frontendServerGateway.delete('/user-profile');
      const { data } = await frontendServerGateway.get('/user-profile');
      const freshProfile = userSchema.parse(data);
      dispatchUserProfile({ type: 'UPDATE', payload: freshProfile });
    })();

    inFlightRef.current = promise;

    try {
      await promise;
    } finally {
      inFlightRef.current = null;
    }
  }, [dispatchUserProfile]);

  return (
    <UserContext.Provider
      value={{
        userProfile,
        dispatchUserProfile,
        isInAppMessageSimpleModalOpen,
        setIsInAppMessageSimpleModalOpen,
        inAppMessageData,
        setInAppMessageData,
        isBoostModalOpen,
        setIsBoostModalOpen,
        boostModalData,
        setBoostModalData,
        isSuggestedProductModalOpen,
        setIsSuggestedProductModalOpen,
        suggestedProductModalData,
        setSuggestedProductModalData,
        currency,
        setCurrency,
        selectedBundle,
        setSelectedBundle,
        isHistoryCheckModalOpen,
        setIsHistoryCheckModalOpen,
        historyCheckModalData,
        setHistoryCheckModalData,
        isSavedSearchModalOpen,
        setIsSavedSearchModalOpen,
        isInstantAlertAvailable,
        setIsInstantAlertAvailable,
        isSellerAnalyticsModalOpen,
        setIsSellerAnalyticsModalOpen,
        sellerAnalyticsModalDisplay,
        setSellerAnalyticsModalDisplay,
        consentOptions,
        isSearchSaved,
        setIsSearchSaved,
        selectedNotificationFrequency,
        setSelectedNotificationFrequency,
        isTrustedDealerModalOpen,
        setIsTrustedDealerModalOpen,
        isBrazeInitialized,
        setIsBrazeInitialized,
        isUserVerified,
        refreshUserVerification,
        isWriteOffModalOpen,
        setIsWriteOffModalOpen,
        performLogout,
      }}
    >
      {children}
    </UserContext.Provider>
  );
}

const useUserContext = () => {
  const context = useContext(UserContext);
  const { data: session, status } = useSession();
  const router = useRouter();
  const asPath = router.asPath;
  const isLoggedInKeycloakUser = !!session?.user;
  const shouldReturnAccessToken = !!session?.user && !session?.error;

  const handleLogin = (url?: string) => {
    signIn('keycloak', {
      callbackUrl:
        typeof url === 'string'
          ? `/signin?callbackUrl=${url}`
          : `/signin?callbackUrl=${asPath}`,
    });
  };

  return {
    ...context,
    handleLogin,
    isAuthenticated: isLoggedInKeycloakUser,
    accessToken: shouldReturnAccessToken ? session?.accessToken : undefined,
    status,
    user: session?.user,
  };
};

// NB: This won't work in Safari <= 15.4, it will fallback to running the callback on every tab
function runOnceAcrossTabs({
  lockName,
  callback,
}: {
  lockName: string;
  callback: () => void | Promise<void>;
}) {
  if (!navigator.locks) {
    return callback();
  }

  // ifAvailable: true means that we get lock === null if the lock is already taken by another tab
  // the default behaviour being to wait for the lock to be available
  return navigator.locks.request(
    lockName,
    { ifAvailable: true },
    async (lock) => {
      if (lock === null) {
        return undefined;
      }

      return callback();
    },
  );
}

export { UserProvider, useUserContext };
